// src/presentation/hooks/useRealTimeChat.ts - Estados de chat en tiempo real

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';
import ApiClient from '../../infrastructure/api/apiClient';

interface OnlineStatus {
  isOnline: boolean;
  lastSeen: Date | null;
  isTyping: boolean;
}

interface UseRealTimeChatOptions {
  chatId?: number;
  participantId?: number;
  pollInterval?: number;
  enableTypingIndicator?: boolean;
}

// Interfaces para respuestas del API
interface UserStatusResponse {
  data?: {
    is_online?: boolean;
    last_seen?: string;
    is_typing?: boolean;
  };
}

interface UseRealTimeChatReturn {
  onlineStatus: OnlineStatus;
  sendTypingIndicator: (isTyping: boolean) => void;
  refreshOnlineStatus: () => Promise<void>;
  isConnected: boolean;
}

/**
 * Hook personalizado para manejar estados de chat en tiempo real
 * Incluye: estado online/offline, indicador de escritura, último visto
 */
export const useRealTimeChat = ({
  chatId,
  participantId,
  pollInterval = 120000, // Aumentado a 120 segundos (2 minutos)
  enableTypingIndicator = true
}: UseRealTimeChatOptions): UseRealTimeChatReturn => {
  
  const { user } = useAuth();
  const [onlineStatus, setOnlineStatus] = useState<OnlineStatus>({
    isOnline: false,
    lastSeen: null,
    isTyping: false
  });
  const [isConnected, setIsConnected] = useState<boolean>(navigator.onLine);
  
  // Referencias para controlar intervalos y timeouts
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<Date>(new Date());
  const isPollingRef = useRef<boolean>(false);

  // Actualizar estado de conexión a internet
  useEffect(() => {
    const handleOnline = () => setIsConnected(true);
    const handleOffline = () => setIsConnected(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  /**
   * Obtiene el estado online del participante desde el backend
   */
  const fetchOnlineStatus = useCallback(async (): Promise<OnlineStatus | null> => {
    if (!participantId || !isConnected || isPollingRef.current) {
      return null;
    }

    try {
      isPollingRef.current = true;
      
      // ✅ CORREGIDO: Sin /api duplicado
      const response = await ApiClient.get<UserStatusResponse>(`/users/${participantId}/status`);
      
      if (response?.data) {
        const { is_online, last_seen, is_typing } = response.data;
        
        return {
          isOnline: Boolean(is_online),
          lastSeen: last_seen ? new Date(last_seen) : null,
          isTyping: Boolean(is_typing)
        };
      }
    } catch (error) {
      console.warn('Error fetching online status:', error);
      
      // Fallback: simular estado basado en patrones realistas
      return simulateOnlineStatus();
    } finally {
      isPollingRef.current = false;
    }

    return null;
  }, [participantId, isConnected]);

  /**
   * Simula estado online cuando no hay conexión al backend
   */
  const simulateOnlineStatus = useCallback((): OnlineStatus => {
    const now = new Date();
    const randomFactor = Math.random();
    
    // 25% probabilidad de estar online
    const isOnline = randomFactor > 0.75;
    
    let lastSeen: Date | null = null;
    if (!isOnline) {
      // Simular última actividad entre 5 min y 4 horas
      const minutesAgo = Math.floor(Math.random() * 240) + 5;
      lastSeen = new Date(now.getTime() - (minutesAgo * 60 * 1000));
    }

    // 5% probabilidad de estar escribiendo si está online
    const isTyping = isOnline && Math.random() > 0.95;

    return {
      isOnline,
      lastSeen,
      isTyping
    };
  }, []);

  /**
   * Refresca el estado online del participante
   */
  const refreshOnlineStatus = useCallback(async (): Promise<void> => {
    if (!participantId) return;

    const status = await fetchOnlineStatus();
    if (status) {
      setOnlineStatus(status);
    }
  }, [fetchOnlineStatus, participantId]);

  /**
   * Envía indicador de escritura al backend
   */
  /**
   * Envía indicador de escritura al backend
   */
  const sendTypingIndicator = useCallback(async (isTyping: boolean): Promise<void> => {
    if (!enableTypingIndicator || !chatId || !user?.id || !isConnected) {
      return;
    }

    try {
      // Limpiar timeout anterior
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // ✅ DETECTAR SI ES VENDEDOR AUTOMÁTICAMENTE
      const isSeller = window.location.pathname.includes('/seller/');
      const baseRoute = isSeller ? '/seller/chats' : '/chats';

      if (isTyping) {
        // Enviar indicador de "escribiendo"
        await ApiClient.post(`${baseRoute}/${chatId}/typing`, {
          user_id: user.id,
          is_typing: true
        });

        // Auto-limpiar después de 3 segundos si no se actualiza
        typingTimeoutRef.current = setTimeout(() => {
          sendTypingIndicator(false);
        }, 3000);
      } else {
        // Enviar indicador de "dejó de escribir"
        await ApiClient.post(`${baseRoute}/${chatId}/typing`, {
          user_id: user.id,
          is_typing: false
        });
      }
    } catch (error) {
      console.warn('Error sending typing indicator:', error);
    }
  }, [enableTypingIndicator, chatId, user?.id, isConnected]);

  /**
   * Detecta actividad del usuario para mantener estado "online"
   */
  const updateLastActivity = useCallback(() => {
    lastActivityRef.current = new Date();
  }, []);

  // Configurar polling del estado online - OPTIMIZADO PARA EVITAR BUCLES
  useEffect(() => {
    if (!participantId || !isConnected) {
      setOnlineStatus({
        isOnline: false,
        lastSeen: null,
        isTyping: false
      });
      return;
    }

    // Limpiar polling anterior
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // Primera carga solo si no hay polling activo
    if (!isPollingRef.current) {
      refreshOnlineStatus();
    }

    // Configurar polling menos agresivo - SOLO si la pestaña está visible
    if (document.visibilityState === 'visible') {
      pollIntervalRef.current = setInterval(() => {
        // Solo hacer polling si la pestaña está visible y no hay operaciones en curso
        if (document.visibilityState === 'visible' && !isPollingRef.current) {
          refreshOnlineStatus();
        }
      }, pollInterval);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [participantId, isConnected]); // Removido pollInterval y refreshOnlineStatus de dependencias

  // NUEVO: Controlar polling basado en visibilidad de la página
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Pausar polling cuando la página no está visible
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          console.log('Polling pausado - página oculta');
        }
      } else if (document.visibilityState === 'visible' && participantId && isConnected) {
        // Reanudar polling cuando la página vuelve a estar visible
        if (!pollIntervalRef.current && !isPollingRef.current) {
          console.log('Reanudando polling - página visible');
          refreshOnlineStatus(); // Actualizar inmediatamente
          
          pollIntervalRef.current = setInterval(() => {
            if (document.visibilityState === 'visible' && !isPollingRef.current) {
              refreshOnlineStatus();
            }
          }, pollInterval);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [participantId, isConnected, pollInterval]);

  // Detectar actividad del usuario
  useEffect(() => {
    if (!enableTypingIndicator) return;

    const events = ['mousemove', 'keypress', 'click', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      window.addEventListener(event, updateLastActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateLastActivity);
      });
    };
  }, [enableTypingIndicator, updateLastActivity]);

  // Limpiar timeouts al desmontar
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Enviar estado "offline" al salir de la página
  useEffect(() => {
    if (!user?.id || !isConnected) return;

    const handleBeforeUnload = () => {
      // 🔧 CORREGIDO: Usar URL base completa para sendBeacon
      const apiBaseUrl = `${import.meta.env.VITE_API_URL || 'https://api.comersia.app'}/api`;
      navigator.sendBeacon(`${apiBaseUrl}/users/${user.id}/activity`, 
        JSON.stringify({ last_seen: new Date().toISOString() })
      );
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handleBeforeUnload();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id, isConnected]);

  return {
    onlineStatus,
    sendTypingIndicator,
    refreshOnlineStatus,
    isConnected
  };
};

/**
 * Hook para manejar el indicador de escritura del usuario actual
 */
export const useTypingIndicator = (chatId?: number) => {
  const { sendTypingIndicator } = useRealTimeChat({ 
    chatId, 
    enableTypingIndicator: true 
  });
  
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const startTyping = useCallback(() => {
    if (!isTyping) {
      setIsTyping(true);
      sendTypingIndicator(true);
    }

    // Resetear timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Parar automáticamente después de 3 segundos sin actividad
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      sendTypingIndicator(false);
    }, 3000);
  }, [isTyping, sendTypingIndicator]);

  const stopTyping = useCallback(() => {
    if (isTyping) {
      setIsTyping(false);
      sendTypingIndicator(false);
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  }, [isTyping, sendTypingIndicator]);

  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  return {
    isTyping,
    startTyping,
    stopTyping
  };
};

export default useRealTimeChat;